import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';
import { useGetDocumentTranslations } from '@utils/api/documents/use-get-document-translations';
import { useGetExportTemplates } from '@utils/api/organizations/use-get-export-templates.tsx';
import { PATHS } from '../../router/paths.ts';
import { CONNECTIONS_TAB_ID } from './profile-connections-tab-id.ts';
import PublishDialog from './publish-dialog/publish-dialog';

interface Props {
  sessionId: string;
  documentId: string;
  /** Which kind the opener (editor/workspace) is biased toward. */
  defaultPayloadKind: 'captions' | 'transcript';
  /** When true, the user can flip between captions and transcript inside the dialog. */
  allowKindToggle: boolean;
  /** Button size — surfaces vary (toolbar button vs row link). */
  size?: 'sm' | 'small';
  /**
   * Fallback caption languages when the translations lookup is still
   * loading. Also used when the opener already knows the document's
   * source language (every editor does).
   */
  fallbackLanguages?: string[];
  /**
   * Controlled open state. When provided, the parent owns the dialog's
   * open/close — used by the editor-header overflow menu at narrow
   * widths so Publish is reachable without the hidden-on-md button.
   * The internal CTA still renders unless `renderTrigger` is false.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * When false, the Publish button itself is not rendered. The dialog
   * still mounts and the parent controls it via `open`/`onOpenChange`.
   * Useful for the overflow-menu entry so it doesn't render a duplicate.
   */
  renderTrigger?: boolean;
}

/**
 * The Publish entry point used from the three export surfaces. Owns
 * the dialog's open/close state (unless `open` is passed) and resolves
 * the caption-language list + transcript templates so each caller does
 * not have to repeat that boilerplate.
 */
export default function PublishAction({
  sessionId,
  documentId,
  defaultPayloadKind,
  allowKindToggle,
  size = 'sm',
  fallbackLanguages = [],
  open: controlledOpen,
  onOpenChange,
  renderTrigger = true
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEnabled: isPublishingEnabled } = useIsPublishingEnabled();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (onOpenChange) onOpenChange(next);
      else setInternalOpen(next);
    },
    [onOpenChange]
  );

  const { data } = useGetDocumentTranslations(sessionId, documentId);
  const languages = (() => {
    if (!data?.result) return fallbackLanguages;
    const codes = new Set<string>();
    if (data.result.source?.language) codes.add(data.result.source.language);
    for (const tr of data.result.translations ?? []) {
      if (tr.language) codes.add(tr.language);
    }
    return codes.size > 0 ? Array.from(codes) : fallbackLanguages;
  })();

  const { data: templatesData } = useGetExportTemplates({
    enabled: open && defaultPayloadKind === 'transcript'
  });
  const transcriptTemplates = useMemo(
    () =>
      (templatesData?.result ?? [])
        .filter(tpl => tpl?.id != null && tpl?.name)
        .map(tpl => ({ id: String(tpl.id), label: tpl.name as string })),
    [templatesData]
  );

  const openConnectWizard = useCallback(
    (connectionId?: string) => {
      setOpen(false);
      // Pass the connection id through so the Connections page auto-
      // opens the wizard in reconnect mode for that specific row.
      // Without it, the user lands on the list and has to find the
      // right connection and dig through the Disconnect / re-add flow.
      const params = new URLSearchParams({ tab: String(CONNECTIONS_TAB_ID) });
      if (connectionId) params.set('reconnect', connectionId);
      navigate(`${PATHS().PROFILE}?${params.toString()}`);
    },
    [navigate, setOpen]
  );

  const onClose = useCallback(() => setOpen(false), [setOpen]);

  if (!isPublishingEnabled) {
    return null;
  }

  return (
    <>
      {renderTrigger && (
        <Button
          size={size}
          variant="secondary"
          icon={<Send size={12} />}
          label={t('publish-cta')}
          onClick={() => setOpen(true)}
          className="hidden md:inline-flex h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
        />
      )}
      <PublishDialog
        isOpen={open}
        onClose={onClose}
        sessionId={sessionId}
        documentId={documentId}
        defaultPayloadKind={defaultPayloadKind}
        availableCaptionLanguages={languages}
        allowKindToggle={allowKindToggle}
        transcriptTemplates={
          transcriptTemplates.length > 0 ? transcriptTemplates : undefined
        }
        onOpenConnectWizard={openConnectWizard}
      />
    </>
  );
}
